/**
 * Auto-add products as nested cart line items based on
 * product metafield custom.auto_add_produkte (configured via bs-auto-add-config.liquid).
 */
(function () {
  window.bsAutoAddMap = window.bsAutoAddMap || {};

  const REMOVED_PREFIX = 'bs-auto-add-removed:';
  let isSyncing = false;
  let previousCartItems = [];

  function getCartUrl() {
    return `${window.themeVariables.routes.cartUrl}.js`;
  }

  function getCartAddUrl() {
    return `${window.themeVariables.routes.cartAddUrl}.js`;
  }

  function getCartChangeUrl() {
    return `${window.themeVariables.routes.cartChangeUrl}.js`;
  }

  async function fetchCart() {
    const response = await fetch(getCartUrl());
    return response.json();
  }

  function normalizeAutoAddVariants(variants) {
    return (variants || [])
      .map((entry) => {
        if (typeof entry === 'object' && entry !== null) {
          return {
            id: Number(entry.id),
            price: Number(entry.price || 0),
          };
        }

        return {
          id: Number(entry),
          price: 0,
        };
      })
      .filter((variant) => variant.id);
  }

  function getAutoAddVariants(productId) {
    return normalizeAutoAddVariants(window.bsAutoAddMap[String(productId)]);
  }

  function isParentLine(item) {
    return !item.parent_relationship;
  }

  function isFreeCartItem(item) {
    return Number(item.final_price ?? item.price ?? 0) === 0;
  }

  function isFreeVariantConfig(variantConfig, cartItem) {
    if (cartItem) {
      return isFreeCartItem(cartItem);
    }

    return Number(variantConfig.price ?? 0) === 0;
  }

  function getRemovedStorageKey(productId, variantId) {
    return `${REMOVED_PREFIX}${productId}:${variantId}`;
  }

  function wasPaidAddonRemoved(productId, variantId) {
    return sessionStorage.getItem(getRemovedStorageKey(productId, variantId)) === '1';
  }

  function markPaidAddonRemoved(productId, variantId) {
    sessionStorage.setItem(getRemovedStorageKey(productId, variantId), '1');
  }

  function clearRemovedFlagsForProduct(productId) {
    for (const variant of getAutoAddVariants(productId)) {
      sessionStorage.removeItem(getRemovedStorageKey(productId, variant.id));
    }
  }

  function trackRemovedPaidAddons(previousItems, currentItems) {
    for (const item of previousItems) {
      if (!item.parent_relationship || isFreeCartItem(item)) {
        continue;
      }

      const stillExists = currentItems.some((cartItem) => cartItem.key === item.key);

      if (stillExists) {
        continue;
      }

      const parentItem = previousItems.find(
        (cartItem) => cartItem.key === item.parent_relationship.parent_key
      );

      if (parentItem) {
        markPaidAddonRemoved(parentItem.product_id, item.variant_id);
      }
    }

    const previousParentIds = new Set(
      previousItems.filter(isParentLine).map((item) => String(item.product_id))
    );
    const currentParentIds = new Set(
      currentItems.filter(isParentLine).map((item) => String(item.product_id))
    );

    for (const productId of previousParentIds) {
      if (!currentParentIds.has(productId)) {
        clearRemovedFlagsForProduct(productId);
      }
    }
  }

  function findNestedChild(cartItems, parentKey, variantId) {
    return cartItems.find(
      (item) =>
        item.parent_relationship?.parent_key === parentKey &&
        Number(item.variant_id) === Number(variantId)
    );
  }

  async function addNestedItem(variantId, quantity, parentLineKey) {
    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity,
            parent_line_key: parentLineKey,
          },
        ],
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[bs-cart-auto-add] Failed to add nested item:', error);
      return false;
    }

    return true;
  }

  async function updateLineQuantity(cartItems, lineKey, quantity) {
    const lineIndex = cartItems.findIndex((item) => item.key === lineKey) + 1;

    if (lineIndex === 0) {
      return false;
    }

    const response = await fetch(getCartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[bs-cart-auto-add] Failed to update line quantity:', error);
      return false;
    }

    return true;
  }

  function dispatchCartEvents(cartContent) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent },
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartContent },
      })
    );
  }

  async function syncAutoAddProducts() {
    if (isSyncing) {
      return;
    }

    isSyncing = true;

    try {
      let cart = await fetchCart();
      let changed = false;

      trackRemovedPaidAddons(previousCartItems, cart.items);

      const parentLines = cart.items.filter(isParentLine);

      for (const parent of parentLines) {
        const autoAddVariants = getAutoAddVariants(parent.product_id);

        if (autoAddVariants.length === 0) {
          continue;
        }

        for (const variantConfig of autoAddVariants) {
          const nestedChild = findNestedChild(cart.items, parent.key, variantConfig.id);
          const isFree = isFreeVariantConfig(variantConfig, nestedChild);

          if (!nestedChild) {
            if (!isFree && wasPaidAddonRemoved(parent.product_id, variantConfig.id)) {
              continue;
            }

            const added = await addNestedItem(variantConfig.id, parent.quantity, parent.key);

            if (added) {
              changed = true;
              cart = await fetchCart();
            }
          } else if (isFree && nestedChild.quantity !== parent.quantity) {
            const updated = await updateLineQuantity(
              cart.items,
              nestedChild.key,
              parent.quantity
            );

            if (updated) {
              changed = true;
              cart = await fetchCart();
            }
          }
        }
      }

      previousCartItems = cart.items;

      if (changed) {
        dispatchCartEvents(cart);
      }
    } catch (error) {
      console.error('[bs-cart-auto-add] Sync failed:', error);
    } finally {
      isSyncing = false;
    }
  }

  document.addEventListener('DOMContentLoaded', syncAutoAddProducts);
  document.addEventListener('cart:updated', syncAutoAddProducts);
})();
